import { useEffect, useState } from 'react';
import { themeApi } from '@/api/theme.api';
import { rememberCollectorModuleContribution } from '@/lib/community-collector.storage';
import type { resolveCollectorContributionContext } from '@/lib/community-collector.storage';
import { collectorTextEntry, submitErrorResult, textContributionPayload, type SubmitResult } from '../initial-data.payloads';
import type { TextAnswer } from '../types';
import { useInitialDataCatalog } from './use-initial-data-catalog';
import { useInitialDataSetup } from './use-initial-data-setup';
import { useQuestionSession } from './use-question-session';
import { useSessionPause } from './use-session-pause';

type CollectorContext = ReturnType<typeof resolveCollectorContributionContext>;

const EMPTY_ANSWER: TextAnswer = { response: '', questionTranslation: '' };

/** Setup, one-question-at-a-time writing and submission for /initial-text. */
export function useInitialTextSession(collectorContext: CollectorContext) {
  const setup = useInitialDataSetup();
  // Written answers were never deduplicated against past contributions: a
  // question can be answered again, so nothing is marked done up front.
  const catalog = useInitialDataCatalog(setup.sectorId, setup.language, { trackSubmitted: false });
  const session = useQuestionSession(catalog.questions, catalog.submittedQuestionIds);
  const pause = useSessionPause({ mode: 'text', setup, catalog, session });

  // Answers typed but not sent yet, per question, for this session only.
  const [answers, setAnswers] = useState<Record<string, TextAnswer>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setAnswers({});
  }, [setup.sectorId, setup.language]);

  const currentId = session.current?.id;
  const answer = (currentId && answers[currentId]) || EMPTY_ANSWER;

  const updateAnswer = (patch: Partial<TextAnswer>) => {
    if (!currentId) return;
    setAnswers((previous) => ({ ...previous, [currentId]: { ...(previous[currentId] ?? EMPTY_ANSWER), ...patch } }));
  };

  const startSession = () => {
    pause.resetCarryOver();
    session.start();
    setup.setStep('session');
  };

  const submitCurrent = async (): Promise<SubmitResult> => {
    const question = session.current;
    const response = answer.response.trim();
    if (!question || !response) return { ok: false };
    setIsSubmitting(true);
    try {
      await themeApi.createContribution(
        textContributionPayload({
          sectorId: setup.sectorId,
          language: setup.language,
          question,
          collectorContext,
          collectionPlace: setup.collectionPlace.place,
          answer,
        }),
        true,
      );
      rememberCollectorModuleContribution(collectorContext, collectorTextEntry(response));
      setAnswers((previous) => {
        const next = { ...previous };
        delete next[question.id];
        return next;
      });
      session.markSubmitted(question.id);
      return { ok: true };
    } catch (error) {
      return submitErrorResult(error, false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    setup,
    // While a paused session is being put back, it reads as still loading.
    catalog: pause.isResuming ? { ...catalog, isLoadingQuestions: true } : catalog,
    session,
    pause,
    answer,
    updateAnswer,
    isSubmitting,
    startSession,
    submitCurrent,
    finish: () => setup.setStep('complete'),
  };
}
